import * as React from 'react';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getItemSummaryCurrency,
    getItemSummaryHighBidEstimate,
    getItemSummaryImageVersion,
    getItemSummaryIsTimedAuction,
    getItemSummaryLotNumber,
    getItemSummaryLowBidEstimate,
    getItemSummaryPhotos,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogCurrentItemId, getLiveCatalogStatus } from '@/redux/modules/console';
import { H4 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { hasVideoStream } from '@/redux/modules/video';
import { openBidConsoleExplainerModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useDebouncedCallback } from 'use-debounce';
import AuctioneerDetails from '../../components/AuctioneerDetails/AuctioneerDetails';
import AuctionVideoStream from '../../components/AuctionVideoStream/AuctionVideoStream';
import CarouselThumbnail from '../../components/CarouselThumbnail/CarouselThumbnail';
import ConsoleSocial from '@/components/ConsoleSocial/ConsoleSocial';
import CTA from '../../components/CTA/CTA';
import CuratorInfo from '@/components/CuratorInfo/CuratorInfo';
import ItemDescription from '@/components/ItemDescription/ItemDescription';
import ItemImageCarousel from './components/ItemImageCarousel/ItemImageCarousel';
import MessageScroller from '@/pages/BiddingConsole/components/MessageScroller/MessageScroller';
import PriceRangeEstimate from './components/PriceRangeEstimate/PriceRangeEstimate';
import styled, { css } from 'styled-components';

type Props = {
    catalogId: number;
};

const MobileBiddingConsole = ({ catalogId }: Props) => {
    const dispatch = useAppDispatch();
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const [biddingLocked, setBiddingLocked] = React.useState<boolean>(true);
    const [lockCTA, setLockCTA] = React.useState<boolean>(false);
    const [currentImageNumber, setCurrentImageNumber] = React.useState<number>(1);
    const [bigVideo, setBigVideo] = React.useState(false);

    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const catalogHasVideoStream = useAppSelector((state) => hasVideoStream(state, catalogId));

    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const highBidEstimate = useAppSelector((state) => getItemSummaryHighBidEstimate(state, itemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, itemId));
    const isTimedAuction = useAppSelector((state) => getItemSummaryIsTimedAuction(state, itemId));
    const lotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const lowBidEstimate = useAppSelector((state) => getItemSummaryLowBidEstimate(state, itemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const debouncedScrollHandler = useDebouncedCallback(() => {
        setLockCTA(window.scrollY >= 423);
    }, 10);

    const toggleBigVideo = React.useCallback(() => {
        setBigVideo((s) => !s);
    }, []);

    React.useEffect(() => {
        window.addEventListener('scroll', debouncedScrollHandler);

        return () => {
            window.removeEventListener('scroll', debouncedScrollHandler);
        };
    }, [debouncedScrollHandler]);

    const beforeChange = React.useCallback((oldIndex: number, newIndex: number) => {
        setCurrentImageNumber(newIndex + 1);
    }, []);

    const toggleBidLock = React.useCallback(() => {
        setBiddingLocked((wasLocked) => !wasLocked);
    }, []);

    const auctionPaused = liveCatalogStatus === 'paused';

    React.useEffect(() => {
        if (isInstantAutopay) {
            dispatch(openBidConsoleExplainerModal());
        }
    }, [dispatch, isInstantAutopay]);

    return (
        <ConsoleContainer>
            <Header $locked={lockCTA}>
                <H4>
                    {lotNumber}
                    :&nbsp;
                    {title}
                </H4>
            </Header>
            <ImageContainer>
                <ItemImageCarousel
                    beforeChange={beforeChange}
                    catalogId={catalogId}
                />
                {catalogHasVideoStream && (
                    <AuctionVideoStream
                        bigVideo={bigVideo}
                        catalogId={catalogId}
                        showMobileConsole
                        toggleBigVideo={toggleBigVideo}
                    />
                )}
                {bigVideo && (
                    <CarouselThumbnail
                        catalogId={catalogId}
                        handleClick={toggleBigVideo}
                        imageVersion={imageVersion}
                        itemId={itemId}
                        mobile
                        photo={photos[currentImageNumber - 1]}
                        sellerId={sellerId}
                    />
                )}
                <OverlayContainer>
                    <ConsoleSocial
                        isPaused={auctionPaused}
                        itemId={itemId}
                    />
                    <ImageCount>
                        <FormattedMessage
                            id="bidding_console.console.description.images_label"
                            values={{
                                currentImageNumber,
                                totalImages: photos.length,
                            }}
                        />
                    </ImageCount>
                    <MessageScroller
                        catalogId={catalogId}
                        convert
                        hideMessages
                    />
                </OverlayContainer>
            </ImageContainer>
            <CTAContainer>
                <CTA
                    biddingLocked={biddingLocked}
                    catalogId={catalogId}
                    locked={lockCTA}
                    toggleBiddingLocked={toggleBidLock}
                />
            </CTAContainer>
            <PriceRangeEstimate
                biddingLocked={biddingLocked}
                catalogId={catalogId}
                currency={currency}
                highBidEstimate={highBidEstimate}
                isTimedAuction={isTimedAuction}
                itemId={itemId}
                lowBidEstimate={lowBidEstimate}
            />
            <ItemDetailsContainer>
                <ItemDescription itemId={itemId} />
                <StyledCuratorInfo catalogId={catalogId} />
                <StyledAuctioneerDetails
                    catalogId={catalogId}
                    sellerId={sellerId}
                />
            </ItemDetailsContainer>
        </ConsoleContainer>
    );
};

export default MobileBiddingConsole;

const ConsoleContainer = styled.div`
    position: relative;
    overflow: auto;
    padding-top: 61px;
`;

const ItemDetailsContainer = styled.div`
    padding: 16px;
`;

const StyledAuctioneerDetails = styled(AuctioneerDetails)`
    margin: 30px 0;
    width: 100%;
`;

const StyledCuratorInfo = styled(CuratorInfo)`
    margin-top: 30px;
`;

const CTAContainer = styled.div`
    min-height: 92px;
    padding-bottom: 11px;
`;

const Header = styled.header<{ $locked: boolean }>`
    width: 100%;
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.white};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2100;
    display: flex;
    height: 60px;
    align-items: center;
    justify-content: center;
    justify-items: center;
    margin: 0;
    padding: 0 16px;
    text-align: center;
    transition: all 0.2s;
    ${({ $locked }) =>
        $locked &&
        css`
            height: 38px;
            > * {
                max-height: 22px;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
        `}
`;

const ImageContainer = styled.div`
    width: 100%;
    height: 420px;
    margin: 0 auto;
    position: relative;
    overflow: hidden;

    @media (max-width: 375px) {
        height: 400px;
    }

    @media (max-width: 320px) {
        height: 300px;
    }
`;

const OverlayContainer = styled.div`
    bottom: 0;
    top: 0;
    left: 0;
    position: absolute;
    z-index: 2000;
    right: 0;
    background-color: transparent;
    pointer-events: none;
    touch-action: none;
`;

const ImageCount = styled.div`
    background-color: ${({ theme }) => theme.colors.shadowOverlay500};
    border-radius: 10px;
    color: ${({ theme }) => theme.colors.white};
    position: absolute;
    top: 10px;
    right: 12px;
    padding: 0 10px;
    z-index: 1;
`;
